import React, { useEffect, useState } from "react";
import { isRenewalAdmin } from "../../utils/folders/isRenewalAdmin.ts";
import { Card, Typography } from "../material-old";
import { Button, Col, Divider, Row, Space, Tag, Tooltip, Alert, message } from "antd";
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { BulbFilled, FolderViewOutlined, SyncOutlined } from "@ant-design/icons";
import { Folder, SigningStatus } from "../../redux/interfaces/folder.types.ts";
import { areMultireportSubfoldersReady } from "../../utils/folders/multireportReadiness.ts";
import { getDaysFromToday } from "../../utils/folders/getDaysFromToday.ts";
import { parseDateToSpanishString } from "../../utils/common/parseDateToSpanishString.ts";
import { BreadcrumbComponent } from "./BreadcrumbComponent.tsx";
import { CardHeader } from "./CardHeader.tsx";
import { TemplateList } from "./TemplateList.tsx";
import { CardAddTemplates } from "./CardAddTemplates.tsx";
import { ShareLink } from "./ShareLink.tsx";
import { ToggleStatus } from "./ToggleStatus.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { Assigned } from "./Assigned.tsx";
import { SendWhatsapp } from "./SendWhatsapp.tsx";
import { ExportFolder } from "./ExportFolder.tsx";
import { hasLeaseContract } from "../../utils/folders/hasLeaseContract.ts";
import {
  PreviousFolioTarget,
  openPreviousFolio,
  previousFolioTarget,
} from "../../utils/folders/previousFolioTarget.ts";
import {
  gray,
  green,
  lightBlue,
  orange,
  red,
} from "../../utils/theme/colors.ts";
import { StartContract } from "./StartContract.tsx";
import { NotifyNow } from "./NotifyNow.tsx";
import { CardStatus } from "./CardStatus.tsx";
import { KeyIcon } from "@heroicons/react/16/solid";
import { PreviewFolder } from "./PreviewFolder.tsx";
import { CompleteFolder } from "./CompleteFolder.tsx";
import { RenewFolder } from "./RenewFolder.tsx";
import { useAppSelector } from "../../redux/hooks.ts";
import { UserRole } from "../../redux/interfaces/auth.types.ts";
import { SignContract } from "./SignContract.tsx";
import { PreviewMultireportWeb } from "./PreviewMultireportWeb.tsx";
import { SlaCountdown } from "../common/SlaCountdown.tsx";
import { TooltipProvider } from "../ui/tooltip.tsx";
import api from "../../api";

interface Props {
  folder: Folder;
  detailed: boolean;
}

export const CardComponent: React.FC<Props> = ({ folder, detailed }) => {
  const isFolderOpen = useParams();
  const { userInfo } = useAppSelector((state) => state.auth);
  const isAdminOrOps: boolean = ["admin", "operations"].some((group) =>
    userInfo?.groups.includes(group as UserRole),
  );
  // MB-379: la renovación es exclusiva de Admin, no de "admin u operaciones".
  const isAdmin: boolean = isRenewalAdmin(userInfo);
  const isCjaUser: boolean | undefined = userInfo?.groups.includes("cja");
  const isAssistantUser = userInfo?.groups.includes("assistant");
  const [subFoldersAreReady, setSubFoldersAreReady] = useState(false);
  const [isIconViewValidated, setIsIconViewValidated] = useState<
    boolean | undefined
  >(false);

  const [signingStatus, setSigningStatus] = useState<SigningStatus>(
    folder.lease_contract_signing_status,
  );
  const [signingDate, setSigningDate] = useState<string | null>(
    folder.lease_contract_sign_date
      ? folder.lease_contract_sign_date
      : "Desconocido",
  );

  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // MB-362 / MB-379: previous contract's folio (may be absent). With `previous_id`
  // the tab opens straight at the folder; with only the number it is resolved first.
  const previousFolio = previousFolioTarget(folder);

  const handleGoToPreviousFolio = (target: PreviousFolioTarget) =>
    openPreviousFolio(target, {
      openInNewTab: (path) => window.open(path, "_blank"),
      resolveAndOpen: resolveAndOpenPreviousFolio,
    });

  // Folios with no `previous_id` (old or manual renewals): translate the number to
  // its folder id (GET /docvault/folders/{n}/folio) and open it in a new tab.
  const resolveAndOpenPreviousFolio = async (folioNumber: string) => {
    // Open the tab synchronously (within the click gesture) so it is not blocked as a
    // popup, then point it at the resolved folder once we have its id.
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get<Folder>(
        `/docvault/folders/${folioNumber}/folio`,
      );
      if (data?.id && win) {
        win.location.href = `/folders/${data.id}`;
      } else {
        win?.close();
        messageApi.error(`No se encontró el folio #${folioNumber}.`);
      }
    } catch {
      win?.close();
      messageApi.error(`No se pudo abrir el folio #${folioNumber}.`);
    }
  };

  const daysCreated = getDaysFromToday(new Date(folder.created_at));
  const createdAtDate = parseDateToSpanishString(new Date(folder.created_at));

  const pending_upload = folder.requirements_summary?.pending_upload || 0;
  const pending_review = folder.requirements_summary?.pending_review || 0;
  const invalid = folder.requirements_summary?.invalid || 0;
  const valid = folder.requirements_summary?.valid || 0;

  const last_updated = folder.last_updated_requirement
    ? parseDateToSpanishString(new Date(folder.last_updated_requirement))
    : "N/A";
  const multireportDeliveredAt = folder.multireport_delivered_at
    ? parseDateToSpanishString(new Date(folder.multireport_delivered_at))
    : "Desconocido";

  const multireportReadyAt = folder.multireport_ready_at
    ? parseDateToSpanishString(new Date(folder.multireport_ready_at))
    : "Desconocido";

  const leaseContractDeliveredAt = folder.lease_contract_generated_at
    ? parseDateToSpanishString(new Date(folder.lease_contract_generated_at))
    : "Desconocido";

  const signingDateLabel = (() => {
    if (!signingDate) return "Desconocido";
    const parsed = new Date(signingDate);
    return isNaN(parsed.getTime())
      ? "Desconocido"
      : parseDateToSpanishString(parsed);
  })();

  const hasContract = hasLeaseContract(folder);

  const handleNavigateDetails = (event: any) => {
    const url = `/folders/${folder.id}`;
    if (event.ctrlKey || event.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  // Los botones de multireporte se habilitan con la MISMA regla que el backend usa para
  // generarlo: solo los subfolders de investigación (MULTIREPORT_RELEVANT_SUBFOLDERS) deben
  // estar `valid`. Antes era una lista negra (todo menos lease-contract-*/owner-representative)
  // que bloqueaba de más: los participantes adicionales (-cja), los arrendadores extra
  // (landlord-pf/pm) y owner-representative-pm desactivaban los botones aunque el backend
  // sí podía generar el multireporte.
  useEffect(() => {
    setSubFoldersAreReady(areMultireportSubfoldersReady(folder));
  }, [folder]);

  useEffect(() => {
    setIsIconViewValidated(
      ((isAdminOrOps || isAssistantUser) &&
        subFoldersAreReady &&
        !!folder.analysis) ||
        (isCjaUser && folder.is_multireport_delivered),
    );
  }, [
    isAdminOrOps,
    subFoldersAreReady,
    folder.analysis,
    userInfo?.groups,
    folder.is_multireport_delivered,
  ]);

  return (
    <>
      {contextHolder}
      <Card className="w-full mx-auto my-12 p-10">
        {!detailed && (
          <div className="flex justify-end">
            <BreadcrumbComponent folder={folder} />
          </div>
        )}
        <div>
          <div className="w-full flex justify-between items-center mb-2">
            <div className="flex items-center gap-3 mr-4">
              <Typography variant="h5" color="black" className="whitespace-nowrap">
                Expediente #{folder.folder_number}
              </Typography>
              {/* MB-362: renewal (same tenant) — IDENTITY chip (icon + teal), not a workflow status */}
              {folder.is_renewal && (
                <span className="inline-flex items-center gap-2">
                  <Tooltip title="Renovación de contrato — mismo inquilino">
                    <Tag
                      icon={<SyncOutlined />}
                      className="font-semibold border-0 m-0 inline-flex items-center"
                      style={{ backgroundColor: "#0d9488", color: "#fff" }}
                    >
                      Renovación
                    </Tag>
                  </Tooltip>
                  {/* Previous folio: may be absent — clickable to open that expediente */}
                  {previousFolio && (
                    <button
                      type="button"
                      onClick={() => handleGoToPreviousFolio(previousFolio)}
                      className="text-xs text-gray-500 whitespace-nowrap transition-colors hover:text-teal-700 hover:underline"
                      title="Ir al expediente del contrato anterior"
                    >
                      Folio anterior
                      {previousFolio.folioNumber && (
                        <>
                          :{" "}
                          <span className="font-medium text-gray-700">
                            #{previousFolio.folioNumber}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </span>
              )}
            </div>
            <div className="ml-auto">
              <div className="flex flex-wrap gap-2 items-center justify-end">
                {folder.is_multireport_delivered ? (
                  <Tag color={"green"}>
                    {`Multireporte Entregado el ${multireportDeliveredAt}`}
                  </Tag>
                ) : folder.is_ready_for_multireport ? (
                  <Space size="small">
                    <Tag color={"blue"}>
                      {`Listo para Multireporte desde ${multireportReadyAt}`}
                    </Tag>
                    <SlaCountdown targetDateTime={folder.multireport_sla_target_at} />
                  </Space>
                ) : null}

                {folder.is_ready_for_contract &&
                  !folder.is_lease_contract_generated && (
                    <Space size="small">
                      <Tag color={"orange"}>
                        {`Listo Para Generar Contrato ${folder.contract_ready_at ? `desde ${parseDateToSpanishString(new Date(folder.contract_ready_at))}` : ""}`}
                      </Tag>
                      <SlaCountdown targetDateTime={folder.contract_sla_target_at} />
                    </Space>
                  )}

                {signingStatus === "signed" && (
                  <Tag color={"purple"}>
                    {`Contrato Firmado el ${signingDateLabel}`}
                  </Tag>
                )}

                {signingStatus === "partial" && (
                  <Tag color={"gold"}>
                    {`Firma Parcial el ${signingDateLabel}`}
                  </Tag>
                )}

                {signingStatus === "cancelled" && (
                  <Tag color={"red"}>
                    {`Contrato Cancelado el ${signingDateLabel}`}
                  </Tag>
                )}

                {signingStatus === "pending" &&
                  folder.is_lease_contract_generated && (
                    <Tag color={"blue"}>
                      {`Contrato Generado el ${leaseContractDeliveredAt}`}
                    </Tag>
                  )}
              </div>
            </div>
          </div>
          <CardStatus folder={folder} />
          <div className="w-full flex justify-between items-center">
            <div className="w-full flex flex-wrap">
              <CardHeader folder={folder} />
            </div>
          </div>
          <TemplateList folder={folder} />
          <Row className="flex justify-between items-center mx-auto">
            <CardAddTemplates folder={folder} />
            <Col>
              <span>
                <BulbFilled className="w-5 h-5" style={{ color: gray }} />
              </span>
              <span>{pending_upload} requisitos pendientes</span>
            </Col>
            <Col>
              <span>
                <BulbFilled className="w-5 h-5" style={{ color: orange }} />
              </span>
              <span>{pending_review} requisitos por revisar</span>
            </Col>
            <Col>
              <span>
                <BulbFilled className="w-5 h-5" style={{ color: red }} />
              </span>
              <span>{invalid} requisitos inválidos</span>
            </Col>
            <Col>
              <span>
                <BulbFilled className="w-5 h-5" style={{ color: green }} />
              </span>
              <span>{valid} requisitos válidos</span>
            </Col>
          </Row>
        </div>
        <div className="my-1">
          <Divider />
        </div>
        <div className="mb-6 text-sm">
          <Row gutter={16}>
            <Col span={16}>
              <div className="flex flex-col gap-2">
                {folder.is_secure_rent_policy_required &&
                  !folder.metadata?.rent_policy && (
                    <Space>
                      <KeyIcon className="h-5 w-5" color={green} />
                      <span>
                        <strong>
                          Este expediente requerirá Póliza de Renta
                        </strong>
                      </span>
                    </Space>
                  )}

                {folder.metadata?.rent_policy && (
                  <Space>
                    <KeyIcon
                      className="h-5 w-5"
                      color={
                        folder.metadata.rent_policy.status === "approved"
                          ? green
                          : folder.metadata.rent_policy.status ===
                              "pre-approved"
                            ? orange
                            : red
                      }
                    />
                    <span>
                      <strong>Póliza de Renta</strong>:{" "}
                      {folder.metadata.rent_policy.status === "approved"
                        ? "Aprobada"
                        : folder.metadata.rent_policy.status === "pre-approved"
                          ? "Pre-aprobada"
                          : "Rechazada"}
                    </span>
                    {folder.metadata.rent_policy.status !== "approved" && (
                      <Tooltip
                        title={
                          folder.metadata.rent_policy.notes &&
                          folder.metadata.rent_policy.notes.length > 0
                            ? folder.metadata.rent_policy.notes.map(
                                (note, index) => (
                                  <div key={index}>• {note}</div>
                                ),
                              )
                            : "No hay notas disponibles"
                        }
                        placement="topLeft"
                      >
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-700" />
                      </Tooltip>
                    )}
                  </Space>
                )}

                <Space>
                  <DocumentTextIcon className="h-5 w-5" color={lightBlue} />
                  <span>
                    <strong>Último requisito actualizado:</strong>{" "}
                    {last_updated}
                  </span>
                </Space>
                <Space>
                  <CalendarDaysIcon className="h-5 w-5" color="red" />
                  <span>
                    <strong>Creado hace:</strong> {daysCreated}{" "}
                    {daysCreated === 1 ? "día" : "días"} ({createdAtDate})
                  </span>
                </Space>
              </div>
            </Col>
            <Col span={8}>
              {folder.metadata?.guarantor?.warning &&
                folder.metadata?.guarantor?.warning.count > 0 && (
                  <Alert
                    message="Advertencia"
                    description={
                      <div>
                        <div>
                          Este fiador/O.S aparece registrado en{" "}
                          <strong>
                            {folder.metadata.guarantor.warning.count}
                          </strong>{" "}
                          expediente(s)
                          {folder.metadata.guarantor.warning.folder_numbers &&
                            folder.metadata.guarantor.warning.folder_numbers
                              .length > 0 && (
                              <>
                                :{" "}
                                <strong>
                                  {folder.metadata.guarantor.warning.folder_numbers
                                    .map((folderNum) => `#${folderNum}`)
                                    .join(", ")}
                                </strong>
                              </>
                            )}
                        </div>
                      </div>
                    }
                    type="warning"
                    showIcon
                  />
                )}
            </Col>
          </Row>
        </div>
        <div className="my-1">
          <Divider />
        </div>
        <div className="flex flex-col text-sm">
          <Space className="mt-2">
            <Assigned folder={folder} type="user" />
          </Space>
          {hasContract && (
            <Space className="mt-2">
              <Assigned folder={folder} type="collaborator" />
            </Space>
          )}
        </div>
        <div className="my-1">
          <Divider />
        </div>
        <div>
          <Row className="w-full justify-between items-center flex flex-wrap">
            <Col className="mb-2">
              <Space wrap>
                {detailed ? (
                  <Button
                    type="dashed"
                    icon={<FolderViewOutlined />}
                    onClick={handleNavigateDetails}
                    className="mb-2"
                  >
                    Ver expediente
                  </Button>
                ) : (
                  <ToggleStatus folder={folder} />
                )}
                {/* MB-379: sólo en la pantalla del folio. En la lista, el
                    botón invitaría a crear expedientes sin haber abierto el que
                    se renueva, y la confirmación habla de ESE expediente. */}
                {!!isFolderOpen.id && (
                  <RenewFolder folder={folder} isAdmin={isAdmin} />
                )}
              </Space>
            </Col>
            <Col className="flex justify-between items-center mb-2">
              <Space className="space-x-5">
                {!!isFolderOpen.id && isAdminOrOps && (
                  <CompleteFolder
                    folder={folder}
                    isAdminOrOps={isAdminOrOps}
                    subFoldersAreReady={subFoldersAreReady}
                  />
                )}
                {!!isFolderOpen.id && (
                  <PreviewFolder
                    folder={folder}
                    isAdminOrOps={isAdminOrOps}
                    isCjaUser={isCjaUser}
                    isAssistantUser={isAssistantUser}
                    subFoldersAreReady={subFoldersAreReady}
                    isIconViewValidated={isIconViewValidated}
                  />
                )}
                {!!isFolderOpen.id && (
                  <PreviewMultireportWeb
                    folder={folder}
                    isAdminOrOps={isAdminOrOps}
                    isCjaUser={isCjaUser}
                    isAssistantUser={isAssistantUser}
                    subFoldersAreReady={subFoldersAreReady}
                    isIconViewValidated={isIconViewValidated}
                  />
                )}
                <Divider type="vertical" />
                <ExportFolder folder={folder} />
                <TooltipProvider delayDuration={200}>
                  <SignContract
                    folder={folder}
                    signingStatus={signingStatus}
                    setSigningStatus={setSigningStatus}
                    signingDate={signingDate}
                    setSigningDate={setSigningDate}
                  />
                </TooltipProvider>
                <StartContract folder={folder} />
                <Divider type="vertical" />
                {/* TODO: if has contract, uncomment this line
              { hasContract && <StartContract folder={folder} /> }*/}
                <ShareLink folder={folder} />
                <SendWhatsapp folder={folder} />
                <NotifyNow folder={folder} />
              </Space>
            </Col>
          </Row>
        </div>
      </Card>
    </>
  );
};
